import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  message,
  Row,
  Col,
  Typography,
  Space,
  Alert,
} from 'antd'
import { SaveOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { PageLoading } from '@/components/PageLoading'
import { getFeatureFlagById, updateFeatureFlag } from '@/api/features'
import { getApiErrorMessage } from '@/utils/error'
import type { FeatureFlagUpdateInput } from '@/types/feature'

const { Title } = Typography

export const FeatureEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()

  // Fetch feature flag details
  const { data: flagData, isLoading } = useQuery({
    queryKey: ['features', id],
    queryFn: () => getFeatureFlagById(id!),
    enabled: !!id,
  })

  // Update feature flag mutation
  const updateMutation = useMutation({
    mutationFn: (values: FeatureFlagUpdateInput) => updateFeatureFlag(id!, values),
    onSuccess: () => {
      message.success('Feature flag updated successfully')
      queryClient.invalidateQueries({ queryKey: ['features'] })
      navigate(`/features/${id}`)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update feature flag'))
    },
  })

  const handleSubmit = (values: FeatureFlagUpdateInput) => {
    updateMutation.mutate(values)
  }

  const handleCancel = () => {
    navigate(`/features/${id}`)
  }

  if (isLoading) {
    return <PageLoading />
  }

  const flag = flagData?.data

  if (!flag) {
    return <Alert message="Feature flag not found" type="error" showIcon />
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Edit Feature Flag
          </Title>
        </Col>
      </Row>

      <Card>
        <Alert
          message="Immutable Fields"
          description="Feature key and scope cannot be changed after creation."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            label: flag.label,
            default_value: flag.default_value,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Feature Key (Read-only)">
                <Input value={flag.feature_key} disabled addonBefore={<code>key:</code>} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="label"
                label="Display Label"
                rules={[{ required: true, message: 'Label is required' }]}
                extra="Human-readable name for this feature"
              >
                <Input placeholder="My Feature Flag" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Scope (Read-only)">
                <Input value={flag.scope === 'user' ? 'User' : 'Facility'} disabled />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Flag Type (Read-only)">
                <Input
                  value={flag.flag_type === 'application' ? 'Application' : 'Config'}
                  disabled
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="default_value"
                label="Default Value"
                valuePropName="checked"
                extra="Default state when not explicitly set"
              >
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginTop: 24 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={updateMutation.isPending}
              >
                Save Changes
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  )
}
